import React, {useMemo} from 'react';
import {Card} from "antd";
import {Cell, Legend, Pie, PieChart, Tooltip} from "recharts";
import {useTranslation} from "react-i18next";
import {useRequest} from "ahooks";
import {CardProps} from "antd/lib/card";
import npiApi from "../../services/api";
import {DevCardTitle} from "./dev-dashboard";

export interface IBoxUploadStatus {
    total: number,
    pending: number,
    not_archived: number,
    not_archived_daily: number,
    pending_daily: number,
    errors: number,
    uploaded: number,
    box_only: number,
}

/**
 * Box upload errors already present in production on 2026-09-09, kept in their own slice so that
 * only the new ones show up in the "Errors" slice.
 * The comparison doubles as the prod-only switch : no other environment ever reaches that count,
 * so they keep displaying all their errors in the single "Errors" slice.
 */
const HISTORICAL_ERRORS = 51143;

const NpiDashboardSyncPieBoxExport = (props: CardProps) => {

    const {t} = useTranslation();

    const {loading, data:status, run} = useRequest<IBoxUploadStatus, any>(npiApi.internal.dev.boxUpload.status);

    const boxUploadPie = useMemo(() => {
        if (!status) {
            return []
        }

        const historicalErrors = status.errors >= HISTORICAL_ERRORS ? HISTORICAL_ERRORS : 0;

        return [
            {name: "Box Only", value: status.box_only, color: "#00b4df"},
            {name: "Uploaded", value: status.uploaded, color: "#19c00d"},
            {name: "Pending", value: status.pending, color: "#FF8400"},
            {name: "Pending daily", value: status.pending_daily, color: "#C08E00"},
            {name: "Not archived NPIs", value: status.not_archived, color: "#686868"},
            {name: "Not archived Daily NPIs", value: status.not_archived_daily, color: "#A6ADB4"},
            ...(historicalErrors > 0
                ? [{name: "Historical errors", value: historicalErrors, color: "#8b4c50"}]
                : []),
            {name: t('INTERNAL.CLIENT.COMMON.ERRORS'), value: status.errors - historicalErrors, color: "#ef000c"},
        ]
    }, [t, status]);

    const boxUploadPercent = useMemo(() => {
        if( !status ) {
            return 0;
        }
        const percentUpload = (status.uploaded + status.box_only) * 100 / status.total;
        return parseFloat(percentUpload.toFixed(2));
    }, [status]);

    return <Card loading={loading} {...props} title={<DevCardTitle title={t('INTERNAL.CLIENT.DASHBOARD.BOX_UPLOADED_IMAGES', {percent:boxUploadPercent})} onReload={run}/>}>
        <PieChart width={250} height={230}>
            <Pie data={boxUploadPie} dataKey="value" innerRadius={30} outerRadius={50} fill="#8884d8" label={true} labelLine={true} minAngle={8}>
                {boxUploadPie.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
            </Pie>
            <Tooltip />
            <br/><br/>
            <Legend/>
        </PieChart>
    </Card>;
};

export default NpiDashboardSyncPieBoxExport;